/**
 * @module tracking/workerTracker
 * Main-thread client for the TrackerWorker.
 *
 * Responsibilities:
 *  - Feature-detect worker + createImageBitmap support.
 *  - Create the module worker via Vite's canonical URL pattern so it is
 *    bundled as a separate chunk automatically (no vite.config changes needed).
 *  - Post init message; resolve when 'ready' arrives; reject on 'fatal' or timeout.
 *  - Pump frames: capture via createImageBitmap(videoEl), transfer to worker.
 *    Backpressure: only one frame in-flight at a time (inFlight guard).
 *  - Call onResult / onFatal on the main thread from worker messages.
 *  - destroy(): terminate worker.
 *
 * Frame guard: skip if videoEl.readyState < HAVE_CURRENT_DATA or videoWidth===0
 * (a 0×0 frame permanently corrupts the MediaPipe WASM graph — same guard
 * as the existing trackingLoop in main.ts).
 */

import { CONFIG } from '../game-config.js';

/** Structured-cloneable landmark point received from the worker. */
export interface WorkerLandmark {
  x: number;
  y: number;
  z: number;
}

/** Callbacks provided to init(). */
export interface WorkerTrackerCallbacks {
  onResult(landmarks: WorkerLandmark[][], tsMs: number, costMs: number): void;
  onFatal(): void;
}

/**
 * Returns true when the runtime supports module workers AND createImageBitmap.
 * Must be checked before calling init().
 */
export function isWorkerTrackingSupported(): boolean {
  return typeof Worker !== 'undefined' && typeof createImageBitmap === 'function';
}

/** Options passed to the TrackerWorker init message. */
export interface WorkerTrackerOptions {
  numHands: number;
  minHandDetectionConfidence: number;
  minHandPresenceConfidence: number;
  minTrackingConfidence: number;
}

let _worker: Worker | null = null;
let _callbacks: WorkerTrackerCallbacks | null = null;
let _inFlight = false;

/**
 * Create and initialise the tracker worker.
 * Resolves when the worker posts 'ready'; rejects on 'fatal' or 20s timeout.
 * Safe to call only once per page load (destroy() first if restarting).
 */
export function init(
  options: WorkerTrackerOptions,
  callbacks: WorkerTrackerCallbacks,
): Promise<void> {
  _callbacks = callbacks;
  _inFlight = false;

  return new Promise<void>((resolve, reject) => {
    // Vite 6 canonical worker pattern: `new Worker(new URL(...), {type:'module'})`.
    // The `vite:worker-import-meta-url` transform plugin detects this exact
    // form and bundles the worker as a separate chunk at build time.
    _worker = new Worker(
      new URL('./trackerWorker.ts', import.meta.url),
      { type: 'module' },
    );

    const initTimeoutMs = CONFIG.tracking.failover.workerInitTimeoutMs;
    const timeout = setTimeout(() => {
      reject(new Error(`[WorkerTracker] Init timed out after ${initTimeoutMs}ms`));
    }, initTimeoutMs);

    _worker.onmessage = (e: MessageEvent<{
      type: 'ready' | 'fatal' | 'result';
      message?: string;
      ts?: number;
      costMs?: number;
      landmarks?: WorkerLandmark[][];
    }>): void => {
      const data = e.data;
      if (data.type === 'ready') {
        clearTimeout(timeout);
        // Swap to the steady-state message handler.
        if (_worker) {
          _worker.onmessage = onSteadyStateMessage;
        }
        resolve();
      } else if (data.type === 'fatal') {
        clearTimeout(timeout);
        reject(new Error(data.message ?? 'Worker fatal'));
      }
    };

    _worker.onerror = (e) => {
      clearTimeout(timeout);
      reject(new Error(`[WorkerTracker] Worker error: ${e.message}`));
    };

    _worker.postMessage({
      type: 'init',
      numHands: options.numHands,
      minHandDetectionConfidence: options.minHandDetectionConfidence,
      minHandPresenceConfidence: options.minHandPresenceConfidence,
      minTrackingConfidence: options.minTrackingConfidence,
      // Passed via the message so the worker chunk does not need to bundle
      // game-config — all tuning still single-sourced from CONFIG here.
      maxConsecutiveFailures: CONFIG.tracking.failover.maxConsecutiveFailures,
    });
  });
}

function onSteadyStateMessage(e: MessageEvent<{
  type: 'ready' | 'fatal' | 'result';
  message?: string;
  ts?: number;
  costMs?: number;
  landmarks?: WorkerLandmark[][];
}>): void {
  const data = e.data;
  _inFlight = false;
  if (data.type === 'result') {
    _callbacks?.onResult(
      data.landmarks ?? [],
      data.ts ?? 0,
      data.costMs ?? 0,
    );
  } else if (data.type === 'fatal') {
    _callbacks?.onFatal();
  }
}

/**
 * Capture one video frame and transfer it to the worker.
 * Returns false (and skips) if:
 *  - A frame is already in-flight (backpressure).
 *  - The video is not ready (readyState < HAVE_CURRENT_DATA or videoWidth===0).
 *  - No worker is active.
 *
 * @param videoEl - The live camera video element.
 * @param tsMs    - Timestamp (ms) from the caller's timing source (e.g. performance.now()).
 */
export function sendFrame(videoEl: HTMLVideoElement, tsMs: number): boolean {
  if (!_worker) return false;
  if (_inFlight) return false;
  if (
    videoEl.readyState < HTMLMediaElement.HAVE_CURRENT_DATA ||
    videoEl.videoWidth === 0
  ) {
    return false;
  }

  _inFlight = true;
  createImageBitmap(videoEl).then((bitmap) => {
    if (!_worker) {
      bitmap.close();
      _inFlight = false;
      return;
    }
    _worker.postMessage({ type: 'frame', bitmap, ts: tsMs }, [bitmap]);
    // inFlight is cleared when the result arrives in onSteadyStateMessage.
  }).catch(() => {
    _inFlight = false;
  });

  return true;
}

/**
 * Terminate the worker. Safe to call multiple times.
 */
export function destroy(): void {
  _worker?.terminate();
  _worker = null;
  _callbacks = null;
  _inFlight = false;
}
